/**
 * Graphical cron editor (ETL scheduling). The operator picks a frequency
 * (ogni N minuti / orario / giornaliero / feriali / settimanale / mensile /
 * personalizzato) and the relevant day/hour/minute controls; the component emits
 * the 5-field cron string + shows a live natural-language preview. "Personalizzato"
 * is the raw-cron escape hatch for anything the graphical modes don't cover.
 *
 * Controlled: `value` is the cron string ("" = no schedule), `onChange(cron)`
 * fires on every edit. State lives as a `CronSpec` (see `lib/cronBuilder`); the
 * incoming value is parsed once on mount + whenever it changes from outside.
 */
import * as React from "react";

import { type CronSpec, buildCron, parseCron } from "@/lib/cronBuilder";
import { describeCron } from "@/lib/cronText";

const DOW = [
  { v: 1, label: "Lun" },
  { v: 2, label: "Mar" },
  { v: 3, label: "Mer" },
  { v: 4, label: "Gio" },
  { v: 5, label: "Ven" },
  { v: 6, label: "Sab" },
  { v: 0, label: "Dom" },
];

const MODES: { v: CronSpec["mode"]; label: string }[] = [
  { v: "minutes", label: "Ogni N minuti" },
  { v: "hourly", label: "Ogni ora" },
  { v: "daily", label: "Ogni giorno" },
  { v: "weekdays", label: "Giorni feriali (Lun–Ven)" },
  { v: "weekly", label: "Giorni della settimana" },
  { v: "monthly", label: "Ogni mese" },
  { v: "custom", label: "Personalizzato (cron)" },
];

const inputCls =
  "h-9 rounded-md border border-border bg-surface px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring";

export function CronEditor({
  value,
  onChange,
  timezone,
}: {
  value: string;
  onChange: (cron: string) => void;
  /** Shown in the preview line only (the cron itself is tz-agnostic). */
  timezone?: string | null;
}) {
  const [spec, setSpec] = React.useState<CronSpec>(() => parseCron(value));

  // Re-parse when the external value changes to something our state doesn't
  // already produce (e.g. a reset / load) — avoids clobbering live edits.
  React.useEffect(() => {
    if (buildCron(spec) !== value.trim()) setSpec(parseCron(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const update = (patch: Partial<CronSpec>) => {
    const next = { ...spec, ...patch };
    setSpec(next);
    onChange(buildCron(next));
  };

  const cron = buildCron(spec);
  const preview = spec.mode === "custom" && !cron ? null : describeCron(cron, timezone);

  const time = (
    <label className="flex items-center gap-2 text-xs">
      <span className="text-muted-foreground">alle</span>
      <input
        type="number"
        min={0}
        max={23}
        value={spec.hour}
        onChange={(e) => update({ hour: Number(e.target.value) })}
        className={`${inputCls} w-16`}
        aria-label="Ora"
      />
      <span className="text-muted-foreground">:</span>
      <input
        type="number"
        min={0}
        max={59}
        value={spec.atMinute}
        onChange={(e) => update({ atMinute: Number(e.target.value) })}
        className={`${inputCls} w-16`}
        aria-label="Minuto"
      />
    </label>
  );

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Frequenza</label>
        <select
          value={spec.mode}
          onChange={(e) => update({ mode: e.target.value as CronSpec["mode"] })}
          className={`${inputCls} w-full`}
        >
          {MODES.map((m) => (
            <option key={m.v} value={m.v}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      {spec.mode === "minutes" && (
        <label className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">ogni</span>
          <input
            type="number"
            min={1}
            max={59}
            value={spec.everyMinutes}
            onChange={(e) => update({ everyMinutes: Number(e.target.value) })}
            className={`${inputCls} w-20`}
            aria-label="Minuti"
          />
          <span className="text-muted-foreground">minuti</span>
        </label>
      )}

      {spec.mode === "hourly" && (
        <label className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">al minuto</span>
          <input
            type="number"
            min={0}
            max={59}
            value={spec.minute}
            onChange={(e) => update({ minute: Number(e.target.value) })}
            className={`${inputCls} w-20`}
            aria-label="Minuto"
          />
        </label>
      )}

      {(spec.mode === "daily" || spec.mode === "weekdays") && time}

      {spec.mode === "weekly" && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {DOW.map((d) => {
              const on = spec.weekdays.includes(d.v);
              return (
                <button
                  key={d.v}
                  type="button"
                  onClick={() =>
                    update({
                      weekdays: on
                        ? spec.weekdays.filter((x) => x !== d.v)
                        : [...spec.weekdays, d.v],
                    })
                  }
                  className={`h-8 w-11 rounded-md border text-xs transition-colors ${
                    on
                      ? "border-accent bg-accent text-accent-foreground"
                      : "border-border bg-surface text-muted-foreground hover:bg-elevated"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          {time}
        </div>
      )}

      {spec.mode === "monthly" && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">il giorno</span>
            <input
              type="number"
              min={1}
              max={31}
              value={spec.dayOfMonth}
              onChange={(e) => update({ dayOfMonth: Number(e.target.value) })}
              className={`${inputCls} w-16`}
              aria-label="Giorno del mese"
            />
          </label>
          {time}
        </div>
      )}

      {spec.mode === "custom" && (
        <div className="space-y-1">
          <input
            value={spec.raw}
            onChange={(e) => update({ raw: e.target.value })}
            placeholder="0 2 * * * (min ora giorno mese giorno-settimana)"
            spellCheck={false}
            className={`${inputCls} w-full font-mono`}
            aria-label="Espressione cron"
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-2 rounded-md bg-elevated px-3 py-2 text-xs">
        <span className="text-muted-foreground">{preview ?? "Nessuna pianificazione"}</span>
        {cron && <code className="font-mono text-muted-foreground">{cron}</code>}
      </div>
    </div>
  );
}
